import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import difference from 'lodash/difference';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { Filter } from '@vk-hr-tek/core/filter';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';
import { History } from '@vk-hr-tek/core/history';
import { QueryService } from '@vk-hr-tek/core/query';

import { EventListItem as Event } from '@app/gen/events';
import { ThunkExtra } from '@app/store';

import {
  completeLoading,
  reviewAdapter,
  ReviewState,
  ReviewWithRootState,
  setError,
  startLoading,
} from '../review.state';
import { GetReviewDto } from '../../dto';
import { EventsListService } from '../../services';

const setFilters = createAction<{
  id: string;
  data: Filter[] | null;
}>('review/setFilters');

const getEventCounts = createAsyncThunk<
  {
    total: number;
    totalMy?: number;
    totalMyTeam?: number;
    finished: number;
  },
  {
    getEventsDto: GetReviewDto;
    availableFilters: Filter[];
  },
  ThunkExtra<ReviewWithRootState>
>(
  'review/getEventCounts',
  async (
    { getEventsDto, availableFilters },
    { rejectWithValue, extra: { inject } },
  ) => {
    try {
      const eventsListService = inject(EventsListService);

      const result = await eventsListService.getEventCounts(
        getEventsDto,
        availableFilters,
      );

      return {
        total: result.total_count,
        totalMy: result.total_count_my,
        totalMyTeam: result.total_count_my_group,
        finished: result.finished_count || 0,
      };
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getEvents = createAsyncThunk<
  {
    data: Event[];
    tags?: string[];
    companyId?: string;
  },
  GetReviewDto,
  ThunkExtra<ReviewWithRootState>
>(
  'review/getEvents',
  async (
    getEventsDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const state = getState().review;
      const eventsService = inject(EventsListService);

      let filters = state.filters;

      if (
        !filters.id ||
        !filters.data ||
        filters.id !== getEventsDto.companyId
      ) {
        filters = {
          data: await eventsService.getFilters({ page: 'goal_management' }),
          id: getEventsDto.companyId || '',
        };

        dispatch(setFilters(filters));
      }

      dispatch(
        getEventCounts({
          getEventsDto,
          availableFilters: filters.data as Filter[],
        }),
      );

      return await eventsService.get(getEventsDto, filters.data as Filter[]);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const updateEvents = createAsyncThunk<
  {
    data: Event[];
  },
  GetReviewDto,
  ThunkExtra<ReviewWithRootState>
>(
  'review/updateEvents',
  async (
    getEventsDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const history = inject<History>(History);
      const filters = getState().review.filters.data;
      const eventListService = inject(EventsListService);

      const filter = inject(QueryService).parse(history.location.search);

      dispatch(
        getEventCounts({
          getEventsDto: {
            ...getEventsDto,
            ...(filter.filters ? { filters: filter.filters } : {}),
          },
          availableFilters: filters || [],
        }),
      );

      const result = await eventListService.get(
        {
          ...getEventsDto,
          ...(filter.filters ? { filters: filter.filters } : {}),
        },
        filters || [],
      );

      return result;
    } catch (err) {
      dispatch(
        showCustomNotification([
          {
            type: 'error',
            title: 'Не удалось обновить заявки',
            message: 'Пожалуйста, попробуйте перезагрузить страницу',
          },
        ]),
      );
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const getAdditionalEvents = createAsyncThunk<
  {
    data: Event[];
  },
  GetReviewDto,
  ThunkExtra<ReviewWithRootState>
>(
  'review/getAdditionalEvents',
  async (
    getEventsDto,
    { rejectWithValue, getState, dispatch, extra: { inject } },
  ) => {
    try {
      const state = getState().review;
      const eventsService = inject(EventsListService);

      let filters = state.filters;

      if (
        !filters.id ||
        !filters.data ||
        filters.id !== getEventsDto.companyId
      ) {
        filters = {
          data: await eventsService.getFilters({ page: 'goal_management' }),
          id: getEventsDto.companyId || '',
        };

        dispatch(setFilters(filters));
      }

      dispatch(
        getEventCounts({
          getEventsDto,
          availableFilters: filters.data || [],
        }),
      );

      return await eventsService.get(getEventsDto, filters.data || []);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const reviewListReducers = (
  builder: ActionReducerMapBuilder<EntityState<Event, string> & ReviewState>,
) => {
  builder.addCase(setFilters, (state, action) => {
    state.filters = action.payload;
  });
  builder.addCase(getEvents.pending, (state) => {
    startLoading(state);
  });
  builder.addCase(getEvents.fulfilled, (state, { payload }) => {
    completeLoading(state);

    state.currentIds = payload.data.map(({ id }) => id);
    reviewAdapter.upsertMany(state, payload.data);
  });

  builder.addCase(getAdditionalEvents.fulfilled, (state, { payload }) => {
    completeLoading(state);

    state.currentIds = [
      ...state.currentIds,
      ...payload.data.map(({ id }) => id),
    ];
    reviewAdapter.upsertMany(state, payload.data);
  });
  builder.addCase(getEvents.rejected, (state, { payload, error, meta }) => {
    if (!meta.aborted) {
      setError(state, { payload, error });
    }
  });
  builder.addCase(updateEvents.fulfilled, (state, { payload, meta }) => {
    completeLoading(state);

    const removedIds = difference(
      meta.arg.sortByIds || [],
      payload.data.map(({ id }) => id),
    );

    reviewAdapter.updateMany(
      state,
      payload.data.map((event) => ({
        id: event.id,
        changes: event,
      })),
    );

    reviewAdapter.removeMany(state, removedIds);
  });

  builder.addCase(getEventCounts.fulfilled, (state, { payload }) => {
    state.total = payload.total;
    state.totalMy = payload.totalMy;
    state.totalMyTeam = payload.totalMyTeam;
    state.finished = payload.finished;
  });
};
